/**
 * Normalize all values into a proper Error for Bugsnag's notify().
 * Returns metadata: null when no normalization was needed (rawValue was already an Error).
 *
 * @param {unknown} rawValue
 * @returns {{ normalizedError: Error, metadata: object|null }}
 */
function normalizeToError(rawValue) {
  // Already a proper Error — pass through as-is
  if (rawValue instanceof Error) {
    return { normalizedError: rawValue, metadata: null };
  }

  let normalizedError;
  const metadata = {};

  if (typeof rawValue === 'string') {
    // Raw string, e.g. throw 'something went wrong'
    normalizedError = new Error(rawValue);
  } else if (rawValue && typeof rawValue === 'object') {
    if ('req' in rawValue && 'err' in rawValue) {
      // fasquest / Travelling SDK wrapper: { req, res, err }
      const requestError = rawValue.err;
      const errorName = requestError?.name || 'UnknownRequestError';
      const errorMessage = requestError?.message || 'Request failed';
      normalizedError = new Error(`${errorName}: ${errorMessage}`);
      normalizedError.name = errorName;

      // Safe, non-sensitive request context only
      const requestOptions = rawValue.req?._opts;
      if (requestOptions) {
        metadata.httpMethod = requestOptions.method;
        metadata.url = requestOptions.url || requestOptions.uri;
        metadata.path = requestOptions.path;
      }
      metadata.hasResponse =
        rawValue.res !== null && rawValue.res !== undefined;
    } else if (rawValue.message) {
      // Error-like object with .message but not instanceof Error
      normalizedError = new Error(String(rawValue.message));
      if (rawValue.name) {
        normalizedError.name = rawValue.name;
      }
      if (rawValue.status) {
        metadata.status = rawValue.status;
      }
    } else {
      // Unrecognizable object shape
      normalizedError = new Error('Non-error value passed to notify()');
    }
  } else {
    // null, undefined, number, boolean, symbol
    normalizedError = new Error('Non-error value passed to notify()');
  }

  metadata.originalType = typeof rawValue;
  return { normalizedError, metadata };
}

export default ({ app }) => {
  const bugsnagClient = app.$bugsnag;
  if (!bugsnagClient) {
    return;
  }

  const originalNotify = bugsnagClient.notify.bind(bugsnagClient);

  bugsnagClient.notify = function(rawValue, ...remainingArgs) {
    const { normalizedError, metadata } = normalizeToError(rawValue);

    if (!metadata) {
      return originalNotify(normalizedError, ...remainingArgs);
    }

    const existingCallback = remainingArgs[0];
    const wrappedCallback =
      typeof existingCallback === 'function'
        ? event => {
            event.addMetadata('Extra Info', metadata);
            return existingCallback(event);
          }
        : event => {
            event.addMetadata('Extra Info', metadata);
          };

    return originalNotify(normalizedError, wrappedCallback);
  };
};
